import { useState } from "react";
import * as listingsAPI from "../../utilities/listings-api";

const maxPhotos = 15;
const maxPhotoSize = 10 * 1024 * 1024; // 10MB, matches the Cloudinary free tier max image file size
const allowedExtensions = ["jpg", "jpeg", "png", "webp", "heic"];

let nextItemId = 0;

function getExtension(fileName) {
  const parts = fileName.split(".");
  return parts[parts.length - 1].toLowerCase();
}

function isAllowedFile(file) {
  const extension = getExtension(file.name);
  let allowed = false;
  for (let i = 0; i < allowedExtensions.length; i++) {
    if (allowedExtensions[i] === extension) {
      allowed = true;
    }
  }
  return allowed;
}

function buildInitialItems(initialPhotos) {
  const initialItems = [];
  if (initialPhotos) {
    for (let i = 0; i < initialPhotos.length; i++) {
      nextItemId = nextItemId + 1;
      initialItems.push({
        id: nextItemId,
        status: "done",
        url: initialPhotos[i].url,
        publicId: initialPhotos[i].publicId,
      });
    }
  }
  return initialItems;
}

function countActivePhotos(currentItems) {
  let count = 0;
  for (let i = 0; i < currentItems.length; i++) {
    count = count + 1;
  }
  return count;
}

// Photo picker used by both the new-listing and edit-listing forms. Manages its
// own upload/reorder/remove state and reports the finished {url, publicId} list
// back to the parent via onChange whenever it changes.
export default function PhotoUploader({ photos, onChange }) {
  const [items, setItems] = useState(() => buildInitialItems(photos));
  const [dragOverZone, setDragOverZone] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [error, setError] = useState("");

  function notifyChange(updatedItems) {
    const donePhotos = [];
    for (let i = 0; i < updatedItems.length; i++) {
      if (updatedItems[i].status === "done") {
        donePhotos.push({ url: updatedItems[i].url, publicId: updatedItems[i].publicId });
      }
    }
    onChange(donePhotos);
  }

  async function uploadOneFile(itemId, file) {
    try {
      const result = await listingsAPI.uploadPhoto(file);
      setItems((currentItems) => {
        const updatedItems = [];
        for (let i = 0; i < currentItems.length; i++) {
          if (currentItems[i].id === itemId) {
            updatedItems.push({ id: itemId, status: "done", url: result.url, publicId: result.publicId });
          } else {
            updatedItems.push(currentItems[i]);
          }
        }
        notifyChange(updatedItems);
        return updatedItems;
      });
    } catch (e) {
      setError(`Upload failed: ${e.message}`);
      setItems((currentItems) => {
        const updatedItems = [];
        for (let i = 0; i < currentItems.length; i++) {
          if (currentItems[i].id !== itemId) {
            updatedItems.push(currentItems[i]);
          }
        }
        return updatedItems;
      });
    }
  }

  function handleFiles(fileList) {
    setError("");
    const filesArray = [];
    for (let i = 0; i < fileList.length; i++) {
      filesArray.push(fileList[i]);
    }

    let currentCount = countActivePhotos(items);
    const newItems = items.slice();

    for (let i = 0; i < filesArray.length; i++) {
      const file = filesArray[i];

      if (currentCount >= maxPhotos) {
        setError(`You can only add up to ${maxPhotos} photos.`);
        break;
      }
      if (!isAllowedFile(file)) {
        setError("Only jpg, jpeg, png, webp, and heic photos are allowed.");
        continue;
      }
      if (file.size > maxPhotoSize) {
        setError("Each photo must be 10MB or smaller.");
        continue;
      }

      nextItemId = nextItemId + 1;
      const itemId = nextItemId;
      const previewUrl = URL.createObjectURL(file);

      newItems.push({
        id: itemId,
        status: "uploading",
        previewUrl: previewUrl,
      });
      currentCount = currentCount + 1;

      uploadOneFile(itemId, file);
    }

    setItems(newItems);
  }

  async function removePhoto(itemId) {
    let itemToRemove = null;
    for (let i = 0; i < items.length; i++) {
      if (items[i].id === itemId) {
        itemToRemove = items[i];
      }
    }
    if (!itemToRemove) return;

    const updatedItems = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].id !== itemId) {
        updatedItems.push(items[i]);
      }
    }
    setItems(updatedItems);
    notifyChange(updatedItems);

    if (itemToRemove.status === "done" && itemToRemove.publicId) {
      try {
        await listingsAPI.deletePhoto(itemToRemove.publicId);
      } catch (e) {
        setError("Could not remove the photo from storage, but it was removed here.");
      }
    }
  }

  function handleDrop(evt) {
    evt.preventDefault();
    setDragOverZone(false);
    handleFiles(evt.dataTransfer.files);
  }

  function handleThumbnailDrop(evt, targetIndex) {
    evt.preventDefault();
    evt.stopPropagation();
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    const reordered = items.slice();
    const draggedItem = reordered[draggedIndex];
    reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, draggedItem);

    setDraggedIndex(null);
    setItems(reordered);
    notifyChange(reordered);
  }

  return (
    <div className="photo-uploader">
      <div
        className={dragOverZone ? "photo-dropzone photo-dropzone-active" : "photo-dropzone"}
        onDragOver={(evt) => { evt.preventDefault(); setDragOverZone(true); }}
        onDragLeave={() => setDragOverZone(false)}
        onDrop={handleDrop}
      >
        <p>Drag and drop photos here, or</p>
        <label className="photo-browse-btn">
          Browse files
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(evt) => handleFiles(evt.target.files)}
            style={{ display: "none" }}
          />
        </label>
        <p className="photo-uploader-note">Up to {maxPhotos} photos, 10MB max each. Drag to reorder — the first photo is the main listing photo.</p>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="photo-thumbnails">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="photo-thumbnail"
            draggable={item.status === "done"}
            onDragStart={() => setDraggedIndex(index)}
            onDragOver={(evt) => evt.preventDefault()}
            onDrop={(evt) => handleThumbnailDrop(evt, index)}
          >
            <img src={item.status === "done" ? item.url : item.previewUrl} alt="Listing" />
            {item.status === "uploading" && <div className="photo-uploading-overlay">Uploading...</div>}
            {index === 0 && item.status === "done" && <span className="photo-main-badge">Main</span>}
            <button type="button" className="photo-remove-btn" onClick={() => removePhoto(item.id)}>
              &times;
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
